import { type DriveFile } from '@/shared/DriveTypes';
import { Add, Close, Edit, OpenInNew, Visibility } from '@mui/icons-material';
import { Divider, Drawer, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { Link } from 'wouter';
import { signOut } from '~/signOut';
import { useActionsDrawerOpen, useMobile } from '~/utils/store';
import { type Profile } from '~/utils/types';
import { getViewInDriveUrl } from '~/utils/utils';
import * as styles from './ActionsDrawer.css';

type Props = {
  wiki: DriveFile | undefined;
  file: DriveFile | undefined;
  user: Profile | undefined;
  canEdit: boolean;
  onAddNew: () => void;
};

export const ActionsDrawer = ({ wiki, file, user, canEdit, onAddNew }: Props) => {
  const isMobile = useMobile();
  const [actionsDrawerOpen, setActionsDrawerOpen] = useActionsDrawerOpen();

  if (!isMobile) return null;

  const close = () => setActionsDrawerOpen(false);

  const showViewButtons = wiki?.properties?.showViewButtons !== 'false';
  const isFolder = file?.mimeType?.endsWith('folder');
  const webViewLink = file?.webViewLink?.replace(/https?:/, '');

  const viewInDriveHref = getViewInDriveUrl(file, user?.email, wiki?.id);

  const primaryActions = [
    ...(!isFolder && showViewButtons
      ? [
          {
            text: canEdit ? 'Edit' : 'View',
            Icon: canEdit ? Edit : Visibility,
            href: webViewLink,
            target: '_blank' as const,
          },
        ]
      : []),
    ...(showViewButtons && viewInDriveHref
      ? [
          {
            text: 'View in Drive',
            src: 'https://www.gstatic.com/images/branding/product/1x/drive_48dp.png',
            alt: 'Drive logo',
            href: viewInDriveHref,
            target: '_blank' as const,
          },
        ]
      : []),
    ...(canEdit
      ? [
          {
            text: 'Add new page',
            Icon: Add,
            onClick: () => {
              close();
              onAddNew();
            },
          },
        ]
      : []),
  ];

  const secondaryActions = [
    ...(user && wiki?.permissions
      ? [
          {
            text: 'Settings',
            href: '/app/settings',
            onClick: close,
          },
        ]
      : []),
    {
      text: 'Tutorials',
      href: '//docs.youneedawiki.com',
      target: '_blank' as const,
    },
    {
      text: 'Feedback',
      href: '//feedback.youneedawiki.com',
      target: '_blank' as const,
    },
    {
      text: 'Support',
      href: '/app/support',
      onClick: close,
    },
    ...(user
      ? [
          {
            text: 'Sign out',
            href: '/api/user/signout',
            onClick: signOut,
            isAnchor: true,
          },
        ]
      : []),
  ];

  return (
    <Drawer anchor="top" open={actionsDrawerOpen} onClose={close}>
      <div className={styles.header}>
        <button type="button" className={styles.closeButton} onClick={close} aria-label="Close">
          <Close />
        </button>
        <h2 className={styles.title}>Actions</h2>
      </div>
      <Divider />
      <List>
        {primaryActions.map(({ text, Icon, src, alt, href, target, onClick }) => {
          const isExternal = target === '_blank';
          const component = onClick && !href ? 'button' : isExternal ? 'a' : Link;
          return (
            <ListItemButton
              key={text}
              component={component}
              {...(href && { href, target })}
              style={{ width: '100%' }}
              onClick={() => {
                if (onClick) onClick();
                if (!isExternal) close();
              }}
            >
              {Icon && (
                <ListItemIcon>
                  <Icon />
                </ListItemIcon>
              )}
              {src && (
                <ListItemIcon>
                  <img className={styles.listIcon} src={src} alt={alt} />
                </ListItemIcon>
              )}
              <ListItemText primary={text} />
              {isExternal && <OpenInNew style={{ fontSize: 16, color: '#8f8f8f', marginLeft: 8 }} />}
            </ListItemButton>
          );
        })}
      </List>
      <Divider />
      <List>
        {secondaryActions.map(({ text, href, target, onClick, isAnchor }) => {
          const isExternal = target === '_blank' || isAnchor;
          const component = isExternal ? 'a' : href ? Link : 'button';
          return (
            <ListItemButton
              key={text}
              component={component}
              {...(href && { href, target })}
              onClick={(e: React.MouseEvent) => {
                if (onClick) onClick(e);
                if (!isExternal && href) close();
              }}
            >
              <ListItemText primary={text} />
              {target === '_blank' && <OpenInNew style={{ fontSize: 16, color: '#8f8f8f', marginLeft: 8 }} />}
            </ListItemButton>
          );
        })}
      </List>
    </Drawer>
  );
};
